<template>
  <Transition name="iwt-cookie">
    <div v-if="visible" class="iwt-cookie-banner" role="dialog" aria-labelledby="cookie-title" aria-live="polite">
      <div class="iwt-cookie-banner__inner">
        <div class="iwt-cookie-banner__text">
          <strong id="cookie-title">{{ t('cookie.title') }}</strong>
          <p>
            {{ t('cookie.body') }}
            {{ t('cookie.readOur') }} <router-link :to="lp('cookie-policy')">{{ t('cookie.cookiePolicy') }}</router-link> {{ t('cookie.and') }}
            <router-link :to="lp('terms-and-conditions')">{{ t('cookie.terms') }}</router-link>.
          </p>
        </div>
        <div class="iwt-cookie-banner__actions">
          <button class="iwt-cookie-btn iwt-cookie-btn--secondary" @click="acceptNecessary">
            {{ t('cookie.necessaryOnly') }}
          </button>
          <button class="iwt-cookie-btn iwt-cookie-btn--primary" @click="acceptAll">
            {{ t('cookie.acceptAll') }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useLanguage } from "@/composables/useLanguage";
import { localizedPath } from "@/router/localeRoutes";

const { t, language } = useLanguage();
const lp = (name) => localizedPath(name, language.value)

const STORAGE_KEY = 'iwt_cookie_consent';
const visible = ref(false);

onMounted(() => {
  if (!localStorage.getItem(STORAGE_KEY)) {
    // Small delay so it doesn't flash during page load
    setTimeout(() => { visible.value = true; }, 800);
  }
});

function acceptAll() {
  localStorage.setItem(STORAGE_KEY, 'all');
  visible.value = false;
}

function acceptNecessary() {
  localStorage.setItem(STORAGE_KEY, 'necessary');
  visible.value = false;
}

// Expose so footer/policy page can trigger a re-show for preference change
defineExpose({ reopen: () => { visible.value = true; } });
</script>

<style lang="scss">
.iwt-cookie-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 99999;
  background-color: var(--assistant-color);
  border-top: 1px solid var(--border-color);
  padding: 20px 40px;

  @media (max-width: 768px) {
    padding: 20px;
  }

  &__inner {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    gap: 30px;
    flex-wrap: wrap;

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
    }
  }

  &__text {
    flex: 1;
    min-width: 240px;

    strong {
      display: block;
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 2px;
      text-transform: uppercase;
      color: var(--heading-color);
      margin-bottom: 6px;
    }

    p {
      font-size: 11px;
      line-height: 1.7;
      color: var(--font-color);
      margin: 0;
      letter-spacing: 0.5px;

      a {
        color: var(--heading-color);
        text-decoration: underline;
        text-underline-offset: 2px;

        &:hover {
          opacity: 0.7;
        }
      }
    }
  }

  &__actions {
    display: flex;
    gap: 12px;
    flex-shrink: 0;
    flex-wrap: wrap;

    @media (max-width: 480px) {
      width: 100%;
    }
  }
}

.iwt-cookie-btn {
  display: inline-block;
  padding: 10px 22px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  border: 1px solid var(--border-color);
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  font-family: var(--body-font);

  @media (max-width: 480px) {
    flex: 1;
    text-align: center;
  }

  &--secondary {
    background: transparent;
    color: var(--font-color);

    &:hover {
      background: var(--border-color);
      color: var(--heading-color);
    }
  }

  &--primary {
    background: var(--heading-color);
    color: var(--bg-color);
    border-color: var(--heading-color);

    &:hover {
      opacity: 0.85;
    }
  }
}

// Slide-up transition
.iwt-cookie-enter-active,
.iwt-cookie-leave-active {
  transition: transform 0.4s cubic-bezier(0.23, 1, 0.32, 1), opacity 0.4s ease;
}
.iwt-cookie-enter-from,
.iwt-cookie-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
